/**
 * Schema Detector - Analyzes blog content to recommend Schema.org types
 * Provides confidence scores and extracts structured data for schemas
 */

/**
 * Analyze content and return recommended schemas with confidence scores
 * @param {string} content - The main content (markdown)
 * @param {string} title - The post title
 * @param {string} excerpt - The post excerpt
 * @returns {Object} ContentAnalysis with recommendedSchemas, confidence, and extractedData
 */
export function analyzeContentForSchemas(content, title, excerpt = '') {
  const analysis = {
    recommendedSchemas: ['article'], // always included
    confidence: { article: 1.0 },
    extractedData: {},
  };

  const fullText = `${title}\n\n${excerpt}\n\n${content}`.toLowerCase();

  // 1. Analyze for How-To
  const howToAnalysis = analyzeHowTo(content, title);
  if (howToAnalysis.confidence > 0.4) {
    analysis.recommendedSchemas.push('howto');
    analysis.confidence.howto = howToAnalysis.confidence;
    analysis.extractedData.steps = howToAnalysis.steps;
  }

  // 2. Analyze for FAQ
  const faqAnalysis = analyzeFAQ(content);
  if (faqAnalysis.confidence > 0.3) {
    analysis.recommendedSchemas.push('faq');
    analysis.confidence.faq = faqAnalysis.confidence;
    analysis.extractedData.faqs = faqAnalysis.faqs;
  }

  // 3. Analyze for Definition
  const definitionAnalysis = analyzeDefinition(content, title);
  if (definitionAnalysis.confidence > 0.3) {
    analysis.recommendedSchemas.push('definition');
    analysis.confidence.definition = definitionAnalysis.confidence;
    analysis.extractedData.definitions = definitionAnalysis.definitions;
  }

  // 4. Analyze for Software Application
  const softwareAnalysis = analyzeSoftware(fullText);
  if (softwareAnalysis.confidence > 0.3) {
    analysis.recommendedSchemas.push('software');
    analysis.confidence.software = softwareAnalysis.confidence;
    analysis.extractedData.softwareFeatures = softwareAnalysis.features;
  }

  return analysis;
}

/**
 * Analyze content for How-To schema
 */
function analyzeHowTo(content, title) {
  let confidence = 0;
  const steps = [];

  const indicators = [
    { pattern: /comment\s+/gi, weight: 0.15 },
    { pattern: /\d+\s+étapes?/gi, weight: 0.2 },
    { pattern: /guide\s+(complet|pratique|détaillé)/gi, weight: 0.15 },
    { pattern: /tutoriel/gi, weight: 0.2 },
    { pattern: /pas\s+à\s+pas/gi, weight: 0.15 },
    { pattern: /suivez\s+ces\s+étapes/gi, weight: 0.15 },
    { pattern: /how\s+to/gi, weight: 0.2 },
    { pattern: /step\s+by\s+step/gi, weight: 0.15 },
  ];

  // Calculate confidence from indicators
  indicators.forEach(({ pattern, weight }) => {
    const matches = (content + title).match(pattern);
    if (matches) {
      confidence += weight * Math.min(matches.length, 3);
    }
  });

  // Extract numbered steps
  const stepPatterns = [
    /^#+\s*(?:étape\s*)?(\d+)[\.\):\s]+(.+)$/gim,
    /^(\d+)[\.\)]\s+(.+)$/gm,
    /^##\s+(.+)$/gm, // H2 headers as steps
  ];

  stepPatterns.forEach(pattern => {
    const matches = [...content.matchAll(pattern)];
    if (matches.length >= 3) {
      confidence += 0.3;
      matches.slice(0, 10).forEach(match => {
        steps.push({
          title: match[2] || match[1],
          content: '', // Could extract content between headers
        });
      });
    }
  });

  return { confidence: Math.min(confidence, 1), steps };
}

/**
 * Analyze content for FAQ schema
 */
function analyzeFAQ(content) {
  let confidence = 0;
  const faqs = [];

  // Count questions
  const questionCount = (content.match(/\?/g) || []).length;
  confidence += Math.min(questionCount * 0.1, 0.4);

  // FAQ keywords
  const faqKeywords = [
    /questions?\s+(fréquentes?|courantes?)/gi,
    /^#+\s*faq/gim,
    /frequently\s+asked/gi,
  ];

  faqKeywords.forEach(pattern => {
    if (pattern.test(content)) confidence += 0.15;
  });

  // Question patterns at start of lines
  const questionPatterns = [
    /^qu[''']est-ce que/gim,
    /^pourquoi/gim,
    /^comment/gim,
    /^what\s+is/gim,
    /^how\s+do/gim,
    /^why\s+/gim,
  ];

  questionPatterns.forEach(pattern => {
    const matches = content.match(pattern);
    if (matches && matches.length >= 2) {
      confidence += 0.1;
    }
  });

  // Extract Q&A pairs (headers ending with ?)
  const qaPairs = content.match(/^#+\s*(.+\?)\s*\n\n?([^#]+?)(?=\n#+|$)/gims);
  if (qaPairs && qaPairs.length >= 2) {
    confidence += 0.3;
    qaPairs.forEach(pair => {
      const match = pair.match(/^#+\s*(.+\?)\s*\n\n?(.+)/is);
      if (match) {
        faqs.push({ 
          question: match[1].trim(), 
          answer: match[2].trim().slice(0, 500) // Limit answer length
        });
      }
    });
  }

  return { confidence: Math.min(confidence, 1), faqs };
}

/**
 * Analyze content for Definition schema
 */
function analyzeDefinition(content, title) {
  let confidence = 0;
  const definitions = [];

  const definitionIndicators = [
    { pattern: /définition\s+de/gi, weight: 0.25 },
    { pattern: /qu[''']est-ce que/gi, weight: 0.2 },
    { pattern: /^le\s+(\w+)\s+est\s+(.+)/gim, weight: 0.2 },
    { pattern: /signifie/gi, weight: 0.15 },
    { pattern: /terme/gi, weight: 0.1 },
    { pattern: /concept/gi, weight: 0.1 },
    { pattern: /glossaire/gi, weight: 0.2 },
    { pattern: /definition\s+of/gi, weight: 0.25 },
    { pattern: /means\s+/gi, weight: 0.1 },
  ];

  definitionIndicators.forEach(({ pattern, weight }) => {
    if (pattern.test(content + title)) {
      confidence += weight;
    }
  });

  // Extract definitions - "X is Y" patterns
  const defPattern = /^le\s+(\w+(?:\s+\w+)*)\s+est\s+(.+?)(?=\n|$)/gim;
  const matches = [...content.matchAll(defPattern)];
  
  if (matches.length > 0 && matches.length <= 5) {
    confidence += 0.2;
    matches.forEach(match => {
      definitions.push({
        term: match[1]?.trim() || '',
        definition: match[2]?.trim().slice(0, 300) || '',
      });
    });
  }

  // If title contains "Qu'est-ce que" or "Définition", extract from first paragraph
  if (/qu[''']est-ce que|définition|what\s+is/i.test(title)) {
    confidence += 0.2;
    const firstParagraph = content.split('\n\n')[0];
    if (firstParagraph && definitions.length === 0) {
      // Extract term from title
      const termMatch = title.match(/(?:qu[''']est-ce que|définition\s+(?:de|du|d[''])|what\s+is)\s+(?:le\s+|la\s+|l['']|a\s+|an\s+)?(.+?)(?:\s*\?|$)/i);
      if (termMatch) {
        definitions.push({
          term: termMatch[1].trim(),
          definition: firstParagraph.replace(/^#.*\n/, '').trim().slice(0, 300),
        });
      }
    }
  }

  return { confidence: Math.min(confidence, 1), definitions };
}

/**
 * Analyze content for Software Application schema
 */
function analyzeSoftware(text) {
  let confidence = 0;
  const features = [];

  const softwareIndicators = [
    { pattern: /\b(logiciel|software)\b/gi, weight: 0.2 },
    { pattern: /\b(application|app|plateforme)\b/gi, weight: 0.15 },
    { pattern: /\b(outil|tool)\b/gi, weight: 0.15 },
    { pattern: /\b(crm|saas|erp)\b/gi, weight: 0.2 },
    { pattern: /\b(télécharger|download|installer|install)\b/gi, weight: 0.15 },
    { pattern: /\bversion\s+\d/gi, weight: 0.2 },
    { pattern: /\bfonctionnalités?\b/gi, weight: 0.15 },
    { pattern: /\b(pricing|tarif|abonnement|subscription)\b/gi, weight: 0.15 },
    { pattern: /\b(free\s+trial|essai\s+gratuit)\b/gi, weight: 0.15 },
    { pattern: /\bapi\b/gi, weight: 0.15 },
  ];

  softwareIndicators.forEach(({ pattern, weight }) => {
    const matches = text.match(pattern);
    if (matches) {
      confidence += weight * Math.min(matches.length, 2);
    }
  });

  // Extract features from bullet points
  const featurePattern = /^[•\-\*]\s+(.+)$/gm;
  const featureMatches = [...text.matchAll(featurePattern)];
  if (featureMatches.length >= 3) {
    confidence += 0.2;
    features.push(...featureMatches.slice(0, 10).map(m => m[1].trim()));
  }

  return { confidence: Math.min(confidence, 1), features };
}

/**
 * Get simple list of recommended schemas
 * @param {string} content 
 * @param {string} title 
 * @param {string} excerpt 
 * @returns {string[]} Array of schema IDs
 */
export function getSchemaRecommendations(content, title, excerpt = '') {
  const analysis = analyzeContentForSchemas(content, title, excerpt);
  return analysis.recommendedSchemas;
}

/**
 * Get confidence scores for each schema type
 * @param {string} content 
 * @param {string} title 
 * @param {string} excerpt 
 * @returns {Object} Map of schema ID to confidence score (0-1)
 */
export function getSchemaConfidence(content, title, excerpt = '') {
  const analysis = analyzeContentForSchemas(content, title, excerpt);
  return analysis.confidence;
}

export default {
  analyzeContentForSchemas,
  getSchemaRecommendations,
  getSchemaConfidence,
};
